/**
 * Post Create Draft Handler
 *
 * Saves and restores in-progress post create drafts from local storage.
 */

import { notifier } from "../../core/notifier/notifier";
import { WysiwygUtils } from "../../wysiwyg/wysiwyg-utils";
import type { PostCreateJsonValue } from "./post-create-api";
import { PostCreateDraftListener } from "./post-create-draft-listener";
import { postCreateForm } from "./post-create-form";
import type { PostCreateDraftFileMeta, PostCreateDraftPayload } from "./post-create-types";

interface PostCreateDraftElements {
    titleInput?: HTMLInputElement | HTMLTextAreaElement | null;
    contentTextarea?: HTMLTextAreaElement | null;
    imageUpload?: HTMLInputElement | null;
    videoUpload?: HTMLInputElement | null;
    [key: string]: PostCreateJsonValue | Element | null | undefined;
}

type DraftJsonRecord = Record<string, PostCreateJsonValue>;

type DraftPayloadInput = Partial<Omit<PostCreateDraftPayload, "review_answers" | "image_files" | "video_files">> & {
    review_answers?: DraftJsonRecord;
    image_files?: Partial<PostCreateDraftFileMeta>[];
    video_files?: Partial<PostCreateDraftFileMeta>[];
};

function isRecord(value: PostCreateJsonValue | DraftJsonRecord | null): value is DraftJsonRecord {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function isDraftFileMeta(value: PostCreateJsonValue | DraftJsonRecord | null): value is PostCreateDraftFileMeta {
    if (!isRecord(value)) return false;

    const source = value as Partial<PostCreateDraftFileMeta>;
    return typeof source.name === "string" && typeof source.size === "number" && typeof source.type === "string";
}

class PostCreateDraftHandler {
    public static STORAGE_PREFIX = "draft:post:create:";
    public static VERSION = 1;
    public static RESTORE_ENABLED = false;

    public form: HTMLFormElement;
    public elements: PostCreateDraftElements;
    private _saveTimer: ReturnType<typeof setTimeout> | null;
    private _restoredOnce: boolean;
    private _onInputOrChange: () => void;
    private _onPageHide: () => void;
    private _onVisibilityChange: () => void;
    private _onExternalSaveRequest: () => void;
    private _onDisable: () => void;
    private _listener: PostCreateDraftListener | null;

    public static isDraftPayload(value: PostCreateJsonValue | DraftJsonRecord | null): value is PostCreateDraftPayload {
        if (!isRecord(value)) return false;

        const source = value as DraftPayloadInput;

        if (typeof source.v !== "number") return false;
        if (source.v !== PostCreateDraftHandler.VERSION) return false;
        if (typeof source.saved_at !== "number") return false;
        if (typeof source.board_id !== "string") return false;
        if (typeof source.title !== "string") return false;
        if (typeof source.content !== "string") return false;

        const snsLinks = source.sns_links;
        if (!Array.isArray(snsLinks) || !snsLinks.every((item) => typeof item === "string")) return false;

        if (
            !Array.isArray(source.sns_shares) ||
            !source.sns_shares.every((item) => item === "instagram" || item === "tiktok")
        ) {
            return false;
        }

        if (typeof source.review_agreement !== "boolean") return false;

        const reviewAnswers = source.review_answers;
        if (!isRecord(reviewAnswers) || !Object.values(reviewAnswers).every((item) => typeof item === "string")) {
            return false;
        }

        if (!Array.isArray(source.image_files) || !source.image_files.every(isDraftFileMeta)) return false;
        if (!Array.isArray(source.video_files) || !source.video_files.every(isDraftFileMeta)) return false;

        return true;
    }

    public constructor(form: HTMLFormElement, elements: PostCreateDraftElements) {
        this.form = form;
        this.elements = elements || {};

        this._saveTimer = null;
        this._restoredOnce = false;

        this._onInputOrChange = this.handleInputOrChange.bind(this);
        this._onPageHide = this.handlePageHide.bind(this);
        this._onVisibilityChange = this.handleVisibilityChange.bind(this);
        this._onExternalSaveRequest = this.handleExternalSaveRequest.bind(this);
        this._onDisable = this.handleDisable.bind(this);
        this._listener = null;
    }

    public static keyForBoard(boardId: string): string {
        return `${PostCreateDraftHandler.STORAGE_PREFIX}${String(boardId || "").trim()}`;
    }

    public static clearForBoard(boardId: string): void {
        try {
            localStorage.removeItem(PostCreateDraftHandler.keyForBoard(boardId));
        } catch (_error) {
            void _error;
        }
    }

    public parseDraftFromStorage(key: string): PostCreateDraftPayload | null {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            const decoded = JSON.parse(raw) as PostCreateJsonValue | DraftJsonRecord | null;
            return PostCreateDraftHandler.isDraftPayload(decoded) ? decoded : null;
        } catch (_error) {
            return null;
        }
    }

    public createReviewAnswersStore(): Record<string, string> {
        return {};
    }

    public getBoardId(): string {
        const byHidden = postCreateForm.getBoardIdInput(this.form);
        if (byHidden && byHidden.value) return String(byHidden.value);

        const byDataset = this.form.getAttribute("data-board-id");
        if (byDataset) return String(byDataset);

        const content =
            this.elements.contentTextarea instanceof HTMLTextAreaElement ? this.elements.contentTextarea : null;
        const byTextarea = content?.getAttribute("data-board-id");
        if (byTextarea) return String(byTextarea);

        return "";
    }

    public attach(): void {
        this.restoreIfAppropriate();
        this._listener?.detach();
        this._listener = new PostCreateDraftListener({
            form: this.form,
            onInputOrChange: this._onInputOrChange,
            onExternalSaveRequest: this._onExternalSaveRequest,
            onDisable: this._onDisable,
            onPageHide: this._onPageHide,
            onVisibilityChange: this._onVisibilityChange,
        });
        this._listener.register();
    }

    public detach(): void {
        this._listener?.detach();
        this._listener = null;

        if (this._saveTimer) {
            clearTimeout(this._saveTimer);
            this._saveTimer = null;
        }
    }

    public hasAnyUserInput(): boolean {
        const titleEl =
            this.elements.titleInput instanceof HTMLInputElement ||
            this.elements.titleInput instanceof HTMLTextAreaElement
                ? this.elements.titleInput
                : null;
        const contentEl =
            this.elements.contentTextarea instanceof HTMLTextAreaElement ? this.elements.contentTextarea : null;

        if (titleEl && String(titleEl.value || "").trim() !== "") return true;
        if (contentEl && String(contentEl.value || "").trim() !== "") return true;

        const snsInputs = postCreateForm
            .getSnsLinkInputs(this.form)
            .map((element) => (element instanceof HTMLInputElement ? element.value.trim() : ""))
            .filter(Boolean);
        if (snsInputs.length > 0) return true;

        const shareToInstagramToggle = postCreateForm.getShareToInstagramToggle(this.form);
        if (shareToInstagramToggle?.checked) return true;

        const shareToTikTokToggle = postCreateForm.getShareToTikTokToggle(this.form);
        if (shareToTikTokToggle?.checked) return true;

        const reviewFields = postCreateForm.getReviewAnswerFields(this.form);
        for (const field of reviewFields) {
            if (field instanceof HTMLInputElement) {
                if (field.type === "radio" && field.checked) return true;
                if (field.type === "hidden" && String(field.value || "").trim() !== "") return true;
                if (field.type === "text" && String(field.value || "").trim() !== "") return true;
            }

            if (field instanceof HTMLTextAreaElement && String(field.value || "").trim() !== "") {
                return true;
            }
        }

        const imageInput = this.elements.imageUpload instanceof HTMLInputElement ? this.elements.imageUpload : null;
        const videoInput = this.elements.videoUpload instanceof HTMLInputElement ? this.elements.videoUpload : null;
        if (imageInput?.files?.length) return true;
        if (videoInput?.files?.length) return true;

        return false;
    }

    public restoreIfAppropriate(): void {
        if (!PostCreateDraftHandler.RESTORE_ENABLED) return;
        if (this._restoredOnce) return;

        const boardId = this.getBoardId();
        if (!boardId) return;

        const parsed = this.parseDraftFromStorage(PostCreateDraftHandler.keyForBoard(boardId));
        if (!parsed) return;
        if (this.hasAnyUserInput()) return;

        this._restoredOnce = true;

        const titleEl =
            this.elements.titleInput instanceof HTMLInputElement ||
            this.elements.titleInput instanceof HTMLTextAreaElement
                ? this.elements.titleInput
                : null;
        const contentEl =
            this.elements.contentTextarea instanceof HTMLTextAreaElement ? this.elements.contentTextarea : null;

        if (titleEl instanceof HTMLInputElement && titleEl.type !== "hidden") {
            titleEl.value = parsed.title;
            this.dispatchSafeEvent(titleEl, "input");
        }

        if (contentEl) {
            contentEl.value = parsed.content;
            this.dispatchSafeEvent(contentEl, "input");
        }

        const reviewAgreement = postCreateForm.getReviewAgreement(this.form);
        if (reviewAgreement) {
            reviewAgreement.checked = parsed.review_agreement;
            this.dispatchSafeEvent(reviewAgreement, "change");
        }

        const shareToInstagramToggle = postCreateForm.getShareToInstagramToggle(this.form);
        if (shareToInstagramToggle) {
            shareToInstagramToggle.checked = parsed.sns_shares.includes("instagram");
            this.dispatchSafeEvent(shareToInstagramToggle, "change");
        }

        const shareToTikTokToggle = postCreateForm.getShareToTikTokToggle(this.form);
        if (shareToTikTokToggle) {
            shareToTikTokToggle.checked = parsed.sns_shares.includes("tiktok");
            this.dispatchSafeEvent(shareToTikTokToggle, "change");
        }

        Object.entries(parsed.review_answers || {}).forEach(([name, value]) => {
            const fields = postCreateForm.getFieldsByName(this.form, name);

            fields.forEach((field) => {
                if (field instanceof HTMLInputElement) {
                    if (field.type === "radio") {
                        field.checked = String(field.value) === String(value);
                        if (field.checked) {
                            this.dispatchSafeEvent(field, "change");
                        }
                        return;
                    }

                    field.value = String(value ?? "");
                    this.dispatchSafeEvent(field, "input");

                    if (field.type === "hidden") {
                        const ratingInput = postCreateForm.getRatingInput(field);
                        if (ratingInput instanceof HTMLElement) {
                            const stars = postCreateForm.getRatingStars(ratingInput);
                            const rating = parseInt(String(value || "0"), 10) || 0;
                            stars.forEach((star, index) => {
                                if (star instanceof HTMLElement) {
                                    star.classList.toggle("selected", index < rating);
                                }
                            });
                        }
                    }
                }

                if (field instanceof HTMLTextAreaElement) {
                    field.value = String(value ?? "");
                    this.dispatchSafeEvent(field, "input");
                }
            });
        });

        const snsLinks = parsed.sns_links || [];
        if (contentEl && snsLinks.length > 0) {
            setTimeout(() => {
                try {
                    WysiwygUtils.mergeSnsLinksIntoEditableList(contentEl, snsLinks);
                } catch (_error) {
                    void _error;
                }
            }, 150);
        }

        const imgCount = (parsed.image_files || []).length;
        const vidCount = (parsed.video_files || []).length;
        if (imgCount > 0 || vidCount > 0) {
            notifier.info("Draft restored. Please re-select attached files (images/videos).", { duration: 6000 });
        } else {
            notifier.info("Draft restored.", { duration: 3000 });
        }
    }

    public save(): void {
        try {
            if (this.form?.hasAttribute?.("data-draft-disabled")) {
                return;
            }
        } catch (_error) {
            void _error;
        }

        const boardId = this.getBoardId();
        if (!boardId) return;

        const key = PostCreateDraftHandler.keyForBoard(boardId);
        if (!this.hasAnyUserInput()) {
            try {
                localStorage.removeItem(key);
            } catch (_error) {
                void _error;
            }
            return;
        }

        const titleEl =
            this.elements.titleInput instanceof HTMLInputElement ||
            this.elements.titleInput instanceof HTMLTextAreaElement
                ? this.elements.titleInput
                : null;
        const contentEl =
            this.elements.contentTextarea instanceof HTMLTextAreaElement ? this.elements.contentTextarea : null;

        const title = titleEl ? String(titleEl.value || "") : "";
        const content = contentEl ? String(contentEl.value || "") : "";

        const snsLinks = postCreateForm
            .getSnsLinkInputs(this.form)
            .map((element) => (element instanceof HTMLInputElement ? element.value.trim() : ""))
            .filter(Boolean);

        const shareToInstagramToggle = postCreateForm.getShareToInstagramToggle(this.form);
        const shareToTikTokToggle = postCreateForm.getShareToTikTokToggle(this.form);
        const snsShares = [
            ...(shareToInstagramToggle?.checked ? ["instagram"] : []),
            ...(shareToTikTokToggle?.checked ? ["tiktok"] : []),
        ];

        const reviewAgreement = postCreateForm.getReviewAgreement(this.form);
        const reviewAnswers = this.createReviewAnswersStore();
        const answerFields = postCreateForm.getReviewAnswerFields(this.form);

        answerFields.forEach((field) => {
            if (field instanceof HTMLInputElement) {
                if (field.type === "radio") {
                    if (field.checked) {
                        reviewAnswers[field.name] = String(field.value);
                    }
                    return;
                }

                reviewAnswers[field.name] = String(field.value || "");
                return;
            }

            if (field instanceof HTMLTextAreaElement) {
                reviewAnswers[field.name] = String(field.value || "");
            }
        });

        const imageInput = this.elements.imageUpload instanceof HTMLInputElement ? this.elements.imageUpload : null;
        const videoInput = this.elements.videoUpload instanceof HTMLInputElement ? this.elements.videoUpload : null;

        const imageFiles = Array.from(imageInput?.files || []).map((file) => ({
            name: file.name,
            size: file.size,
            type: file.type,
        }));
        const videoFiles = Array.from(videoInput?.files || []).map((file) => ({
            name: file.name,
            size: file.size,
            type: file.type,
        }));

        const payload: PostCreateDraftPayload = {
            v: PostCreateDraftHandler.VERSION,
            saved_at: Date.now(),
            board_id: boardId,
            title,
            content,
            sns_links: snsLinks,
            sns_shares: snsShares,
            review_agreement: !!reviewAgreement?.checked,
            review_answers: reviewAnswers,
            image_files: imageFiles,
            video_files: videoFiles,
        };

        try {
            localStorage.setItem(key, JSON.stringify(payload));
        } catch (_error) {
            void _error;
        }
    }

    private handleInputOrChange(): void {
        if (this._saveTimer) clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(() => {
            this._saveTimer = null;
            this.save();
        }, 500);
    }

    private handlePageHide(): void {
        this.save();
    }

    private handleVisibilityChange(): void {
        if (document.visibilityState === "hidden") {
            this.save();
        }
    }

    private handleExternalSaveRequest(): void {
        this.save();
    }

    private handleDisable(): void {
        try {
            this.form?.setAttribute?.("data-draft-disabled", "true");
        } catch (_error) {
            void _error;
        }
        this.detach();
    }

    private dispatchSafeEvent(target: HTMLElement, eventName: string): void {
        try {
            target.dispatchEvent(new Event(eventName, { bubbles: true }));
        } catch (_error) {
            void _error;
        }
    }
}

export { PostCreateDraftHandler };
